import { getApiClient } from "@src/api";
import { libraryQueryKeys } from "@src/components/library/queries/query-keys";
import { graphql } from "@src/gql";
import type { LibraryScope } from "@src/gql/graphql";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

export const libraryEntitiesQuery = graphql(/* GraphQL */ `
	query libraryEntities(
		$scope: LibraryScope
		$type: String
		$search: String
		$first: Int
		$after: String
	) {
		library {
			entities(
				scope: $scope
				type: $type
				search: $search
				first: $first
				after: $after
			) {
				nodes {
					id
					entityId
					entityType
					baseKind
					name
					description
					ownerId
					ownerEmail
					ownerName
					ownerAvatarUrl
					collaborators {
						userId
						userEmail
						permission
						userName
						avatarUrl
					}
					status
					isPublished
					shareKey
					icon {
						type
						value
					}
					createdAt
					updatedAt
					isPinned
				}
				pageInfo {
					hasNextPage
					hasPreviousPage
					startCursor
					endCursor
				}
				totalCount
			}
		}
	}
`);

export const libraryEntityTypeCountsQuery = graphql(/* GraphQL */ `
	query GetLibraryEntityTypeCounts($scope: LibraryScope, $search: String) {
		library {
			all: entities(scope: $scope, search: $search) {
				totalCount
			}
			board: entities(scope: $scope, type: "board", search: $search) {
				totalCount
			}
			card: entities(scope: $scope, type: "card", search: $search) {
				totalCount
			}
			chat: entities(scope: $scope, type: "chat", search: $search) {
				totalCount
			}
		}
	}
`);

export interface UseLibraryEntitiesParams {
	scope?: LibraryScope | null;
	type?: string | null;
	search?: string | null;
	limit?: number;
}

export function useLibraryEntities({
	scope,
	type,
	search,
	limit = 24,
}: UseLibraryEntitiesParams = {}) {
	const client = getApiClient();

	return useInfiniteQuery({
		queryKey: libraryQueryKeys.entities.list({
			scope,
			type,
			search,
			limit,
		}),
		queryFn: async ({ pageParam }) => {
			const result = await client.query({
				query: libraryEntitiesQuery,
				variables: {
					scope: scope ?? undefined,
					type: type ?? undefined,
					search: search?.trim() ? search.trim() : undefined,
					first: limit,
					after: pageParam,
				},
			});

			if (result.error) {
				throw new Error(
					result.error.graphQLErrors[0]?.message ?? result.error.message,
				);
			}

			const entitiesData = result.data?.library?.entities;
			return {
				nodes: entitiesData?.nodes ?? [],
				pageInfo: entitiesData?.pageInfo ?? {
					hasNextPage: false,
					hasPreviousPage: false,
					startCursor: null,
					endCursor: null,
				},
				totalCount: entitiesData?.totalCount ?? 0,
			};
		},
		initialPageParam: undefined as string | undefined,
		getNextPageParam: (lastPage) =>
			lastPage.pageInfo.hasNextPage
				? (lastPage.pageInfo.endCursor ?? undefined)
				: undefined,
		staleTime: 1000 * 30,
	});
}

export interface UseLibraryEntityTypeCountsParams {
	scope?: LibraryScope | null;
	search?: string | null;
}

export function useLibraryEntityTypeCounts({
	scope,
	search,
}: UseLibraryEntityTypeCountsParams = {}) {
	const client = getApiClient();

	return useQuery({
		queryKey: libraryQueryKeys.entityTypeCounts.list({
			scope,
			search,
		}),
		queryFn: async () => {
			const result = await client.query({
				query: libraryEntityTypeCountsQuery,
				variables: {
					scope: scope ?? undefined,
					search: search?.trim() ? search.trim() : undefined,
				},
			});

			if (result.error) {
				throw new Error(
					result.error.graphQLErrors[0]?.message ?? result.error.message,
				);
			}

			const counts = result.data?.library;
			return [
				{ entityType: "all", count: counts?.all?.totalCount ?? 0 },
				{ entityType: "board", count: counts?.board?.totalCount ?? 0 },
				{ entityType: "card", count: counts?.card?.totalCount ?? 0 },
				{ entityType: "chat", count: counts?.chat?.totalCount ?? 0 },
			];
		},
		staleTime: 1000 * 30,
	});
}
